import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { AsyncState } from "@/components/common/Loader";
import api from "@/lib/api";
import { Pencil, ExternalLink, SlidersHorizontal } from "lucide-react";

export default function AdminPagesList() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/pages").then((r) => setPages(r.data)).catch(() => setPages([])).finally(() => setLoading(false));
  }, []);

  return (
    <AdminLayout>
      <div>
        <h1 className="display-title text-4xl">Pages</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Visually edit page sections. Changes are saved as drafts and go live only when you commit.
        </p>
      </div>

      <div className="mt-6 space-y-2" data-testid="admin-pages-list">
        <AsyncState loading={loading} empty={pages.length === 0} emptyTitle="No pages found" emptyDescription="Pages will appear here once created.">
          {pages.map((p) => (
            <div
              key={p.key}
              data-testid={`admin-page-row-${p.key}`}
              className="flex items-center gap-3 rounded-lg border border-border bg-card p-3 transition-colors hover:bg-muted/40"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="truncate text-sm font-medium">{p.name}</p>
                  {p.has_draft
                    ? <span className="rounded-full bg-[hsl(48_96%_53%)] px-2.5 py-0.5 text-[11px] font-semibold uppercase text-black" data-testid={`page-status-${p.key}`}>Draft</span>
                    : <span className="rounded-full bg-[hsl(142_71%_45%)] px-2.5 py-0.5 text-[11px] font-semibold uppercase text-black" data-testid={`page-status-${p.key}`}>Published</span>}
                </div>
                <p className="mt-0.5 truncate text-[11px] uppercase tracking-wide text-muted-foreground">
                  {p.url}{p.updated_by ? ` · edited by ${p.updated_by}` : ""}
                </p>
              </div>

              <div className="flex shrink-0 items-center gap-0.5">
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="View live page"
                  data-testid={`admin-page-view-${p.key}`}
                  className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
                <Link
                  to={`/admin/pages/${p.key}`}
                  title="Form editor"
                  data-testid={`admin-page-form-${p.key}`}
                  className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <SlidersHorizontal className="h-4 w-4" />
                </Link>
                <Link
                  to={`${p.url}${p.url.includes("?") ? "&" : "?"}edit=1`}
                  title="Visual edit"
                  data-testid={`admin-page-edit-${p.key}`}
                  className="ml-1 inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-2 text-xs font-semibold uppercase text-white transition-colors hover:bg-[hsl(var(--brand-red-deep))]"
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Link>
              </div>
            </div>
          ))}
        </AsyncState>
      </div>
    </AdminLayout>
  );
}
